/**
 * Opening search results for a set of pro numbers.
 *
 * `multi=true` is what makes /searchresults treat the query as a LIST of terms and
 * render one card per match, rather than one search for a single string. It is used for
 * a single pro too: a one-card result page is the same card the pro page opens with, and
 * a caller should not have to branch on how many pros it happens to have.
 */

/** Open search results for these pros in a new tab. No-op when the list is empty. */
export function openProSearch(pros: Array<string | number>): void {
  const terms = pros.map((p) => String(p).trim()).filter(Boolean);
  if (!terms.length) return;
  const url = `/searchresults?q=${encodeURIComponent(terms.join(','))}&multi=true`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Search results for every order on a masterpro, as a path.
 *
 * V1's `fv_masterpro` (formatview.pl:3256) opens
 * `savecustomsearch&searchtype=masterpro` in a new tab — a Custom Search on ONE field,
 * which is exactly the `searchBy` mode here. Note `q` is newline-joined for searchBy,
 * not comma-joined, because some custom-search fields hold commas.
 */
export function masterproSearchPath(masterpro: string | number): string {
  const term = String(masterpro).trim();
  if (!term) return '';
  return `/searchresults?${new URLSearchParams({ q: term, searchBy: 'masterpro' })}`;
}

/** Open that search in a new tab. No-op without a masterpro. */
export function openMasterproSearch(masterpro: string | number): void {
  const url = masterproSearchPath(masterpro);
  if (url) window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Search results for every order on ANY of these masterpros, in a new tab.
 *
 * The plural twin of `openMasterproSearch`, and the reason it exists is the
 * Accounting drills: the Pending Invoicing / Invoicing Hold families draw ONE ROW PER
 * MASTERPRO over a representative pro (accounting_dashboards.py::row_type_for), so the
 * rows in front of the user are not orders and a pro-keyed mass action there would
 * reach the representative only. Handing the masterpros to Custom Search expands them
 * back into the real order set, where Change All means what it says.
 *
 * Newline-joined because that is what `searchBy` mode splits on (see above) — commas
 * are legal inside some custom-search values.
 */
export function openMasterprosSearch(masterpros: Array<string | number>): void {
  const terms = Array.from(
    new Set(masterpros.map((m) => String(m).trim()).filter(Boolean)),
  );
  if (!terms.length) return;
  const url = `/searchresults?${new URLSearchParams({ q: terms.join('\n'), searchBy: 'masterpro' })}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Search results opened straight onto ONE pro's detail — the
 * `/searchresults?q=<pro>&pro=<pro>` form that replaced the old `/pro/:pronumber`
 * deep link.
 *
 * The `pro` param is what lands the user on the detail instead of a result list, and
 * it is what makes the link work at all for a deleted/closed order: a bare `q` search
 * returns nothing there until "Also search closed" is ticked.
 *
 * Templates need no special case — `cms.template.pronumber` already stores the "T"
 * prefix (e.g. `T83111`), and search matches on it directly.
 */
export function proDetailPath(pro: string | number): string {
  const term = String(pro).trim();
  if (!term) return '';
  const enc = encodeURIComponent(term);
  return `/searchresults?q=${enc}&pro=${enc}`;
}
